import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserAttendance, getUserVisitHistory } from '../../actions/users';
import moment from 'moment';

export default function MyAttendance() {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [attendanceData, setAttendanceData] = useState({});
    const dispatch = useDispatch();
    const isUserAttendanceLoading = useSelector(state => state.loading.isUserAttendanceLoading);
    const userAttendanceByIds = useSelector(state => state.user.userAttendanceByIds);
    const userVisitHistoryByIds = useSelector(state => state.user.userVisitHistoryByIds);
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const user_id = newLoggedUserData?.user?.id;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const userAttendance = useMemo(() => {
        return userAttendanceByIds[user_id] || [];
    }, [userAttendanceByIds, user_id]);

    useEffect(() => {
        const from = (() => {
            const d = new Date();
            d.setMonth(d.getMonth() - 2);
            d.setHours(0, 0, 0, 0);
            return d;
        })();
        const to = (() => {
            const d = new Date();
            d.setMonth(d.getMonth() + 2);
            d.setHours(23, 59, 59, 999);
            return d;
        })();
        dispatch(getUserAttendance({ userId: user_id, from, to }));
    }, [user_id, dispatch]);

    useEffect(() => {
        if (userAttendance && userAttendance.length > 0) {
            const transformedData = {};
            userAttendance.forEach((record) => {
                if (record.date && record.working_status) {
                    const dateObj = moment(record.date).add(5, 'hours').add(30, 'minutes');
                    const dateKey = dateObj.format('YYYY-MM-DD');

                    let status = null;
                    if (record.working_status === 'WORKING') {
                        status = 'present';
                    } else if (record.working_status === 'LEAVE' || record.working_status === 'LEAVE_APPROVED') {
                        status = 'leave';
                    } else if (record.working_status === 'HALF_DAY') {
                        status = 'half_day';
                    }

                    if (status) {
                        transformedData[dateKey] = {
                            status,
                            date: dateKey
                        };
                    }
                }
            });
            setAttendanceData(transformedData);
        } else {
            setAttendanceData({});
        }
    }, [userAttendance]);

    const getDateRange = () => {
        const start = new Date(today);
        start.setMonth(start.getMonth() - 2);
        const end = new Date(today);
        end.setMonth(end.getMonth() + 2);
        return { start, end };
    };

    const { start: minDate, end: maxDate } = getDateRange();

    const getCalendarDays = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];

        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            days.push(new Date(year, month, day));
        }

        return days;
    };

    const isDateInRange = (date) => {
        if (!date) return false;
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        return checkDate >= minDate && checkDate <= maxDate;
    };

    const changeMonth = (direction) => {
        const newMonth = new Date(currentMonth);
        newMonth.setMonth(newMonth.getMonth() + direction);

        if (newMonth >= minDate && newMonth <= maxDate) {
            setCurrentMonth(newMonth);
        }
    };

    const formatMonthYear = (date) => {
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };

    const getDateStatus = (date) => {
        if (!date) return null;
        const dateKey = moment(date).add(5, 'hours').add(30, 'minutes').format('YYYY-MM-DD');
        return attendanceData[dateKey];
    };

    const getUpcomingLeaves = () => {
        const leaves = Object.values(attendanceData)
            .filter(record => record.status === 'leave')
            .map(record => ({
                ...record,
                dateObj: new Date(record.date + 'T00:00:00.000Z')
            }))
            .sort((a, b) => a.dateObj - b.dateObj);

        const todayIST = new Date(today.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
        todayIST.setHours(0, 0, 0, 0);

        const upcomingLeaves = leaves.filter(leave => leave.dateObj >= todayIST).slice(0, 5);

        return upcomingLeaves;
    };

    const calendarDays = getCalendarDays(currentMonth);
    const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    const upcomingLeaves = getUpcomingLeaves();

    return (
        <div style={{ backgroundColor: '#d0dfff' }} className="overflow-x-hidden">
            <div className="max-w-4xl mx-auto p-2 sm:p-4 relative">
                {isUserAttendanceLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/90 backdrop-blur-sm z-50">
                        <div className="flex flex-col items-center space-y-4">
                            <div className="relative">
                                <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-spin"></div>
                                <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
                            </div>
                            <div className="text-center">
                                <p className="text-blue-600 font-medium">Loading attendance data...</p>
                                <p className="text-gray-500 text-sm">Please wait while we fetch the attendance data</p>
                            </div>
                        </div>
                    </div>
                ) : null}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-3 sm:gap-5">
                    <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200 w-full max-w-full overflow-hidden">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-2 w-full">
                            <div className="flex items-center gap-2 flex-1 min-w-0 w-full">
                                <button
                                    className="text-[22px] w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg text-gray-700 text-lg font-medium transition-colors flex-shrink-0"
                                    onClick={() => changeMonth(-1)}
                                    disabled={currentMonth <= minDate}
                                >
                                    ‹
                                </button>
                                <h2 className="text-sm sm:text-base font-semibold text-gray-800 truncate w-full text-center">{formatMonthYear(currentMonth)}</h2>
                                <button
                                    className="text-[22px] w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg text-gray-700 text-lg font-medium transition-colors flex-shrink-0"
                                    onClick={() => changeMonth(1)}
                                    disabled={new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1) > maxDate}
                                >
                                    ›
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-7 gap-1 w-full">
                            {weekDays.map((day, i) => (
                                <div key={i} className="select-none text-center text-[10px] sm:text-[12px] font-semibold text-gray-500 uppercase tracking-wide py-1 sm:py-1.5">{day}</div>
                            ))}

                            {calendarDays.map((date, index) => {
                                if (!date) {
                                    return <div key={`empty-${index}`} className="aspect-square"></div>;
                                }

                                const dateStatus = getDateStatus(date);
                                const isInRange = isDateInRange(date);
                                const isToday = date.toDateString() === today.toDateString();

                                let dayClasses = "aspect-square flex items-center justify-center rounded-lg text-[10px] sm:text-[11px] font-medium transition-all border-2 relative min-w-0 ";

                                if (!isInRange) {
                                    dayClasses += "opacity-20 bg-gray-50 text-gray-300 border-transparent ";
                                } else if (dateStatus?.status === 'present') {
                                    dayClasses += "bg-emerald-500 text-white font-semibold shadow-sm border-transparent ";
                                } else if (dateStatus?.status === 'leave') {
                                    dayClasses += "bg-red-400 text-white font-semibold shadow-sm border-transparent ";
                                } else if (dateStatus?.status === 'half_day') {
                                    dayClasses += "bg-amber-400 text-white font-semibold shadow-sm border-transparent ";
                                } else {
                                    dayClasses += "bg-gray-50 text-gray-700 border-transparent ";
                                }

                                if (isToday && !dateStatus) {
                                    dayClasses += "border-2 border-indigo-500 bg-indigo-200 text-indigo-700 font-bold shadow-sm ";
                                } else if (isToday && dateStatus) {
                                    dayClasses += "ring-2 ring-indigo-400 ring-offset-1 shadow-md ";
                                }

                                return (
                                    <div
                                        key={date.toISOString()}
                                        className={dayClasses}
                                    >
                                        <span>{date.getDate()}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200 h-fit w-full">
                        <h3 className="text-[12px] font-bold text-gray-800 mb-3 pb-2 border-b-2 border-red-300">Upcoming Leaves</h3>
                        {upcomingLeaves.length > 0 ? (
                            <div className="flex flex-col gap-2.5">
                                {upcomingLeaves.map((leave) => {
                                    return (
                                        <div key={leave.date} className="flex items-center gap-2.5 p-2.5 bg-red-50 rounded-lg border-l-4 border-red-300 hover:bg-red-100 transition-all hover:shadow-sm">
                                            <div className="text-lg flex-shrink-0">📅</div>
                                            <div className="flex-1">
                                                <div className="text-xs font-semibold text-gray-800 mb-0.5">
                                                    {leave.dateObj.toLocaleDateString('en-US', {
                                                        weekday: 'short',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric'
                                                    })}
                                                </div>
                                                <div className="text-[10px] text-red-600 uppercase font-semibold tracking-wider">Leave</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-8 text-center">
                                <div className="text-4xl mb-2 opacity-50">📭</div>
                                <p className="text-[12px] text-gray-400 font-medium">No upcoming leaves</p>
                            </div>
                        )}

                        <div className="mt-5 pt-4 border-t border-gray-200">
                            <h4 className="text-[12px] font-bold text-gray-800 mb-2.5 uppercase tracking-wide">Color Guide</h4>
                            <div className="flex flex-wrap gap-2.5">
                                <div className="flex items-center gap-2.5">
                                    <span className="w-5 h-5 bg-emerald-500 rounded flex-shrink-0 shadow-sm"></span>
                                    <span className="text-[11px] text-gray-700 font-medium">Present</span>
                                </div>
                                <div className="flex items-center gap-2.5">
                                    <span className="w-5 h-5 bg-amber-400 rounded flex-shrink-0 shadow-sm"></span>
                                    <span className="text-[11px] text-gray-700 font-medium">Half Day</span>
                                </div>
                                <div className="flex items-center gap-2.5">
                                    <span className="w-5 h-5 bg-red-400 rounded flex-shrink-0 shadow-sm"></span>
                                    <span className="text-[11px] text-gray-700 font-medium">Leave</span>
                                </div>
                                <div className="flex items-center gap-2.5">
                                    <span className="w-5 h-5 bg-indigo-200 border-2 border-indigo-500 rounded flex-shrink-0 shadow-sm"></span>
                                    <span className="text-[11px] text-gray-700 font-medium">Today</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}